import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * Which job is this, and which work order does it belong to.
 *
 * Accept, Reject and Clarify each committed the vendor to a decision while
 * naming nothing but the action — the job was a uuid in the address bar. A
 * vendor could reject the wrong work order and never know. The painter screen
 * already carried this block inline; it now shares this one, so there is a
 * single description of an assignment rather than one per screen.
 *
 * Reads GET /v1/vendor-assignments/:id. If the caller has already loaded that
 * row it passes it in and no second request is made.
 */

/** Mirrors the job context on the backend assignment-detail response. */
export interface AssignmentJobContext {
  readonly jobNumber: string | null;
  readonly customerName: string | null;
  readonly dealerCode: string | null;
  readonly dealerName: string | null;
  readonly siteAddress: string | null;
  readonly city: string | null;
  readonly pinCode: string | null;
  readonly contactPerson: string | null;
  readonly contactMobile: string | null;
  readonly district: string | null;
  readonly state: string | null;
  readonly areaRequested: number | null;
  readonly areaUnit: string | null;
  readonly currentStatus: string | null;
  readonly remarks: string | null;
}

export interface AssignmentSummary {
  readonly jobRequestId: string;
  readonly assignmentCode: string | null;
  readonly woNumber: string | null;
  readonly allocatedQuantity: number | null;
  readonly unitOfMeasure: string | null;
  readonly priority: string | null;
  readonly expectedStartDate: string | null;
  readonly expectedCompletionDate: string | null;
  readonly status: string | null;
  readonly specialInstructions: string | null;
  readonly job: AssignmentJobContext | null;
}

/** The dealer's shop, written the way an address is read. */
export function siteLine(job: AssignmentJobContext | null): string {
  if (!job) return '—';
  const parts = [job.siteAddress, job.city, job.district, job.state, job.pinCode];
  return parts.filter((p) => p && String(p).trim().length > 0).join(', ') || '—';
}

export function dealerLine(job: AssignmentJobContext | null): string {
  if (!job) return '—';
  const name = job.dealerName ?? job.customerName;
  if (name && job.dealerCode) return `${name} · ${job.dealerCode}`;
  return name ?? job.dealerCode ?? '—';
}

export function quantityLine(
  a: Pick<AssignmentSummary, 'allocatedQuantity' | 'unitOfMeasure' | 'job'> | null,
  decimal: (value: string | number | null | undefined) => string,
): string {
  if (!a) return '—';
  if (a.allocatedQuantity != null) return `${decimal(a.allocatedQuantity)} ${a.unitOfMeasure ?? ''}`.trim();
  if (a.job?.areaRequested != null) return `${decimal(a.job.areaRequested)} ${a.job.areaUnit ?? ''}`.trim();
  return '—';
}

export function JobAssignmentSummary({ assignmentId, header }: {
  readonly assignmentId: string | undefined;
  readonly header?: AssignmentSummary | null;
}): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { dayRange } = useBusinessDay();
  const { decimal } = useBusinessNumber();
  const [fetched, setFetched] = useState<AssignmentSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const provided = header !== undefined;

  useEffect(() => {
    if (provided || !assignmentId) return;
    let alive = true;
    (async () => {
      try {
        const r = await apiRequest<AssignmentSummary>(`/v1/vendor-assignments/${assignmentId}`, { token });
        if (alive) { setFetched(r); setError(null); }
      } catch (e) {
        // Say the job could not be named rather than render a blank panel that
        // reads as "no work order".
        if (alive) setError((e as Error).message);
      }
    })();
    return () => { alive = false; };
  }, [assignmentId, token, provided]);

  const a = provided ? header ?? null : fetched;

  if (error) return <p role="alert" aria-label="job-summary-error">{error}</p>;
  if (!a) return <p aria-label="job-summary-loading">{t('common.loading')}</p>;

  return (
    <section aria-label="job-summary">
      <h2 aria-label="summary-job-number">{a.job?.jobNumber ?? a.jobRequestId}</h2>
      <dl aria-label="job-summary-fields">
        <dt>{t('vops.painter.ctxWorkOrder')}</dt>
        <dd aria-label="summary-work-order">{a.woNumber ?? '—'}</dd>

        <dt>{t('vops.jobDetail.assignmentCode')}</dt>
        <dd aria-label="summary-assignment-code">{a.assignmentCode ?? '—'}</dd>

        <dt>{t('vops.painter.ctxDealer')}</dt>
        <dd aria-label="summary-dealer">{dealerLine(a.job)}</dd>

        <dt>{t('vops.painter.ctxSite')}</dt>
        <dd aria-label="summary-site">{siteLine(a.job)}</dd>

        <dt>{t('vops.painter.ctxContact')}</dt>
        <dd aria-label="summary-contact">
          {[a.job?.contactPerson, a.job?.contactMobile].filter(Boolean).join(' · ') || '—'}
        </dd>

        <dt>{t('vops.painter.ctxQuantity')}</dt>
        <dd aria-label="summary-quantity">{quantityLine(a, decimal)}</dd>

        <dt>{t('vops.painter.ctxWindow')}</dt>
        <dd aria-label="summary-window">
          {dayRange(a.expectedStartDate, a.expectedCompletionDate)}
        </dd>

        <dt>{t('vops.common.status')}</dt>
        <dd aria-label="summary-status">{a.status ?? '—'}</dd>

        <dt>{t('vops.painter.ctxScope')}</dt>
        <dd aria-label="summary-scope">{a.job?.remarks ?? a.specialInstructions ?? '—'}</dd>
      </dl>
    </section>
  );
}
